import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';
import { Theme } from '../ui/theme';
import { GameState } from '../systems/GameState';
import { PetCareEngine } from '../systems/PetCareEngine';
import { EggHatcher, EGG_HATCH_DURATION_MS } from '../systems/EggHatcher';
import { PetRenderer, PET_SHEET_KEY } from '../systems/PetRenderer';
import { PETS_BY_ID } from '../data/pets';
import { svgStringToTexture } from '../utils/svgToTexture';
import { Button } from '../ui/Button';
import { ToastManager } from '../ui/ToastManager';
import { EconomyEngine } from '../systems/EconomyEngine';
import { AudioManager } from '../systems/AudioManager';

export class PetRoomScene extends Phaser.Scene {
  private petSprite: Phaser.GameObjects.Image | Phaser.GameObjects.Sprite | null = null;
  private toast!: ToastManager;
  private gaugeContainer: Phaser.GameObjects.Container | null = null;
  private listContainer: Phaser.GameObjects.Container | null = null;
  private headerContainer: Phaser.GameObjects.Container | null = null;
  private listScrollX = 0;
  private listMaxScrollX = 0;
  private listTargetX = 0;
  private listMask: Phaser.Display.Masks.GeometryMask | null = null;
  private listDragging = false;
  private listDragStartX = 0;
  private listDragStartScroll = 0;
  private listScrollEvents: { off: () => void }[] = [];

  constructor() { super('PetRoomScene'); }

  async create(): Promise<void> {
    AudioManager.setScene(this);
    AudioManager.playBgm('petroom');
    this.scene.launch('HUDScene');
    this.cameras.main.setBackgroundColor('#fff5f8');
    if (this.textures.exists('bg_petroom')) {
      const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_petroom');
      // 화면을 채우도록 스케일 — cover 방식
      const sx = GAME_WIDTH / bg.width;
      const sy = GAME_HEIGHT / bg.height;
      const s = Math.max(sx, sy);
      bg.setScale(s).setDepth(-100);
      // 가독성용 살짝 톤 다운
      this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xfff5f8, 0.55).setDepth(-99);
    }
    this.toast = new ToastManager(this);

    // 시간 흐름 적용 + 알 부화 체크
    PetCareEngine.applyTimeDecay();
    const hatched = EggHatcher.checkReadyEggs();

    this.add.text(GAME_WIDTH/2, 72, '🐾 펫룸', { fontFamily: Theme.font.family, fontSize: '34px', fontStyle: '800', color: Theme.hex.text }).setOrigin(0.5);

    await this.refresh();

    // 부화된 알이 있으면 부화 축하 모달 표시
    if (hatched.length > 0) {
      await this._showHatchCelebration(hatched);
    }

    new Button(this, 100, GAME_HEIGHT-50, '← 홈', () => {
      this.cameras.main.fadeOut(200);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        this.scene.stop('HUDScene');
        this.scene.start('HomeScene');
      });
    }, { width: 120, height: 48, style: 'secondary' });
    new Button(this, GAME_WIDTH-220, GAME_HEIGHT-50, '🥚 알 부화 (💰15000)', () => this.buyEgg(), { width: 240, height: 48, style: 'gold' });
  }

  private async refresh(): Promise<void> {
    const activeId = GameState.data.activePetId;
    const active = GameState.data.ownedPets.find(p => p.petId === activeId);

    if (this.headerContainer) this.headerContainer.destroy();
    if (this.gaugeContainer) this.gaugeContainer.destroy();
    if (this.listContainer) this.listContainer.destroy();
    if (this.petSprite) this.petSprite.destroy();

    this.headerContainer = this.add.container(0,0);
    this.gaugeContainer = this.add.container(0,0);
    this.listContainer = this.add.container(0,0);

    if (!active) {
      const t = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2, '아직 펫이 없어요!', { fontFamily: 'Pretendard Variable', fontSize: '24px', color: '#7a5b80' }).setOrigin(0.5);
      this.headerContainer.add(t);
      return;
    }

    const meta = PETS_BY_ID[active.petId];

    // Pet emote based on happiness — 겹치지 않게 가운데 정렬로 한 줄에 배치
    const happinessEmote = active.happiness >= 70 ? '😊' : active.happiness >= 40 ? '😐' : '😢';
    const happinessColor = active.happiness >= 70 ? '#22cc66' : active.happiness >= 40 ? '#f0a020' : '#e54e87';
    const headerText = this.add.text(GAME_WIDTH/2, 115, `${meta?.name ?? '펫'}  Lv.${active.level}`, {
      fontFamily: Theme.font.family, fontSize: '22px', fontStyle: '800', color: Theme.hex.text,
    }).setOrigin(0.5);
    const headerW = headerText.width;
    const emoteText = this.add.text(GAME_WIDTH/2 + headerW/2 + 24, 115, happinessEmote, { fontSize: '28px' }).setOrigin(0.5);
    this.tweens.add({ targets: emoteText, scaleX: 1.2, scaleY: 1.2, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
    const happyLabel = this.add.text(GAME_WIDTH/2 + headerW/2 + 48, 115, `${Math.round(active.happiness)}%`, {
      fontFamily: Theme.font.family, fontSize: '14px', fontStyle: '700', color: happinessColor,
    }).setOrigin(0, 0.5);
    this.headerContainer.add([headerText, emoteText, happyLabel]);

    // 게이지 4개
    this.drawGauge(140, 180, '❤️ 행복', active.happiness, 0xff6fa1);
    this.drawGauge(140, 240, '🍖 배고픔', active.hunger, 0xffc94c);
    this.drawGauge(140, 300, '🛁 청결', active.cleanliness, 0x6fd4c4);
    this.drawGauge(140, 360, '⭐ 경험', active.exp, 0x9b6bff);

    // 펫 스프라이트 — AI PNG 우선, SVG fallback
    if (meta) {
      // 배경에 묻히지 않게 펫 뒤에 부드러운 후광 + 카드 배경
      const petCx = GAME_WIDTH/2 + 100;
      const petCy = 350;
      const halo = this.add.graphics();
      halo.fillStyle(0xffffff, 0.55);
      halo.fillCircle(petCx, petCy + 30, 240);
      halo.fillStyle(0xffffff, 0.85);
      halo.fillCircle(petCx, petCy + 30, 200);
      halo.lineStyle(4, 0xff6fa1, 0.6);
      halo.strokeCircle(petCx, petCy + 30, 220);
      this.gaugeContainer!.add(halo);

      // 시트 애니메이션 우선 (dog/cat/rabbit/hamster)
      if (PetRenderer.hasSheet(meta.species) && this.textures.exists(PET_SHEET_KEY)) {
        const sprite = this.add.sprite(petCx, petCy, PET_SHEET_KEY,
          PetRenderer.getSheetFrame(meta.species, 'idle')).setScale(2.8);
        const idleKey = PetRenderer.getAnimKey(meta.species, 'idle');
        if (this.anims.exists(idleKey)) sprite.play(idleKey);
        this.petSprite = sprite;
      } else {
        const spriteKey = PetRenderer.getSpriteKey(meta.species);
        let useKey: string;
        let scale: number;
        if (this.textures.exists(spriteKey)) {
          useKey = spriteKey;
          scale = 1.5;
        } else {
          const svg = PetRenderer.renderSVG(meta.species);
          useKey = `pet_room_${active.petId}`;
          await svgStringToTexture(this, useKey, svg);
          scale = 8;
        }
        this.petSprite = this.add.image(petCx, petCy, useKey).setScale(scale);
      }
      this.tweens.add({ targets: this.petSprite, y: this.petSprite.y - 8, duration: 800, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
    }

    // 액션 버튼 4개
    const actY = 540;
    const petX = GAME_WIDTH/2 + 100;
    const petY = 350;
    new Button(this, 220, actY, '🍖 밥', () => {
      PetCareEngine.feed(active.petId);
      AudioManager.playSfx('pet_happy');
      this._spawnHearts(petX, petY);
      this.refresh();
    }, { width: 160, height: 56, style: 'gold' });
    new Button(this, 420, actY, '🎾 놀기', () => {
      PetCareEngine.play(active.petId);
      AudioManager.playSfx('pet_happy');
      this._spawnSparkles(petX, petY);
      this.refresh();
    }, { width: 160, height: 56, style: 'primary' });
    new Button(this, 620, actY, '🛁 씻기', () => {
      PetCareEngine.bath(active.petId);
      AudioManager.playSfx('pet_happy');
      this._spawnSparkles(petX, petY);
      this.refresh();
    }, { width: 160, height: 56, style: 'secondary' });

    // 펫 리스트 (가로 스크롤 가능) — 옷장과 비슷한 스크롤 구조
    const listY = 680;
    const listViewportLeft = 140;
    const listViewportRight = GAME_WIDTH - 60;
    const listViewportTop = listY - 56;
    const listViewportH = 130;
    const itemSpacing = 100;
    const eggSpacing = 110; // 알은 단축 버튼 때문에 좀 더 넓게
    const startX = listViewportLeft + 60;

    // 마스크
    const maskShape = this.make.graphics({});
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(listViewportLeft, listViewportTop, listViewportRight - listViewportLeft, listViewportH);
    this.listMask = maskShape.createGeometryMask();
    this.listContainer!.setMask(this.listMask);

    // 누적 컨텐츠 너비
    let cursorX = startX;

    for (const p of GameState.data.ownedPets) {
      const x = cursorX;
      cursorX += itemSpacing;
      const m = PETS_BY_ID[p.petId];
      const isActive = p.petId === activeId;

      // 1. 카드 배경 먼저 (가장 뒤)
      const bg = this.add.graphics();
      bg.fillStyle(isActive ? 0xfffadc : 0xffffff, 1);
      bg.fillRoundedRect(x - 45, listY - 45, 90, 90, 12);
      bg.lineStyle(3, isActive ? 0xf0a020 : 0xff6fa1, 1);
      bg.strokeRoundedRect(x - 45, listY - 45, 90, 90, 12);
      this.listContainer!.add(bg);

      // 2. 펫 썸네일 — sheet > PNG > SVG fallback
      if (m) {
        const spriteKey = PetRenderer.getSpriteKey(m.species);
        if (PetRenderer.hasSheet(m.species) && this.textures.exists(PET_SHEET_KEY)) {
          const petImg = this.add.sprite(x, listY - 8, PET_SHEET_KEY,
            PetRenderer.getSheetFrame(m.species, 'idle')).setScale(0.6);
          this.listContainer!.add(petImg);
          if (isActive) {
            this.tweens.add({ targets: petImg, scaleX: 0.7, scaleY: 0.7, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
          }
        } else if (this.textures.exists(spriteKey)) {
          const petImg = this.add.image(x, listY - 8, spriteKey).setScale(0.32);
          this.listContainer!.add(petImg);
          if (isActive) {
            this.tweens.add({ targets: petImg, scaleX: 0.36, scaleY: 0.36, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
          }
        } else {
          const svg = PetRenderer.renderSVG(m.species);
          const key = `pet_thumb_${p.petId}`;
          const speciesEmoji: Record<string, string> = {
            dog: '🐶', cat: '🐱', rabbit: '🐰', hamster: '🐹', dragon: '🐲', hedgehog: '🦔', wolf: '🐺', whale: '🐳', chick: '🐥', ferret: '🐾', alpaca: '🦙', capybara: '🐹', cow: '🐮', goat: '🐐', crab: '🦀', bunny_alien: '👽', phoenix: '🔥',
            bird: '🐦', unicorn: '🦄', penguin: '🐧', fox: '🦊', panda: '🐼',
            bear: '🐻', pig: '🐷', turtle: '🐢', fish: '🐟',
          };
          const placeholder = this.add.text(x, listY - 8, speciesEmoji[m.species] ?? '🐾', { fontSize: '32px' }).setOrigin(0.5);
          this.listContainer!.add(placeholder);

          svgStringToTexture(this, key, svg).then(() => {
            if (!this.scene.isActive()) return;
            if (!this.listContainer) return;
            placeholder.destroy();
            const petImg = this.add.image(x, listY - 8, key).setScale(1.0);
            this.listContainer!.add(petImg);
            if (isActive) {
              this.tweens.add({ targets: petImg, scaleX: 1.05, scaleY: 1.05, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
            }
          }).catch(() => { /* placeholder remains */ });
        }
      }

      // 3. 이름·레벨 배지·클릭 영역 (펫 위에 그려짐)
      const label = this.add.text(x, listY + 32, m?.name ?? '?', {
        fontFamily: Theme.font.family, fontSize: '11px', fontStyle: '700', color: Theme.hex.text,
      }).setOrigin(0.5);
      const lvBadge = this.add.rectangle(x + 32, listY - 32, 26, 18, 0x9b6bff);
      const lvText = this.add.text(x + 32, listY - 32, `${p.level}`, {
        fontFamily: Theme.font.family, fontSize: '10px', fontStyle: '900', color: '#ffffff',
      }).setOrigin(0.5);
      const hit = this.add.rectangle(x, listY, 90, 90, 0x000000, 0).setInteractive({ useHandCursor: true });
      hit.on('pointerdown', () => {
        AudioManager.playSfx('click');
        PetCareEngine.setActive(p.petId);
        this.refresh();
      });
      this.listContainer!.add([label, lvBadge, lvText, hit]);
    }

    // 알 표시 (펫 실루엣 미리보기 + 단축 버튼)
    GameState.data.hatchingEggs.forEach((e, i) => {
      const x = cursorX;
      cursorX += eggSpacing;
      const bg = this.add.rectangle(x, listY, 80, 80, 0xffabd0);
      bg.setStrokeStyle(3, 0xff6fa1);
      const remainMin = Math.max(0, Math.ceil((e.readyAt - Date.now()) / 60000));
      const remainHr = Math.floor(remainMin / 60);

      const eggTxt = this.add.text(x, listY - 14, '🥚', { fontSize: '22px' }).setOrigin(0.5);

      const petMeta = PETS_BY_ID[e.petId];
      let silhouette: Phaser.GameObjects.Text | null = null;
      if (petMeta) {
        const speciesEmoji: Record<string, string> = {
          dog: '🐶', cat: '🐱', rabbit: '🐰', hamster: '🐹', dragon: '🐲', hedgehog: '🦔', wolf: '🐺', whale: '🐳', chick: '🐥', ferret: '🐾', alpaca: '🦙', capybara: '🐹', cow: '🐮', goat: '🐐', crab: '🦀', bunny_alien: '👽', phoenix: '🔥',
          bird: '🐦', unicorn: '🦄', penguin: '🐧', fox: '🦊', panda: '🐼',
          bear: '🐻', pig: '🐷', turtle: '🐢', fish: '🐟',
        };
        const emoji = speciesEmoji[petMeta.species] ?? '🐾';
        silhouette = this.add.text(x, listY + 10, emoji, { fontSize: '18px' }).setOrigin(0.5).setAlpha(0.45);
        this.listContainer!.add(silhouette);
        this.tweens.add({ targets: silhouette, angle: 5, duration: 600, yoyo: true, repeat: -1, ease: 'Sine.InOut' });
      }

      // 남은 시간 (시간/분 단위로)
      const timeStr = remainMin <= 0 ? '✨ 준비!'
                    : remainHr >= 1 ? `${remainHr}시간 ${remainMin % 60}분`
                    : `${remainMin}분`;
      const timeTxt = this.add.text(x, listY + 30, timeStr, {
        fontFamily: 'Pretendard Variable', fontSize: '11px', fontStyle: '700',
        color: remainMin <= 0 ? '#ff6fa1' : '#3a2540',
      }).setOrigin(0.5);

      // 진행 막대
      const barW = 70;
      const totalDur = EGG_HATCH_DURATION_MS;
      const elapsed = totalDur - Math.max(0, e.readyAt - Date.now());
      const progress = Math.min(1, elapsed / totalDur);
      const barBg = this.add.rectangle(x, listY + 44, barW, 6, 0xeeeeee);
      barBg.setStrokeStyle(1, 0xccaacc);
      const barFill = this.add.rectangle(x - barW/2 + (barW * progress)/2, listY + 44, barW * progress, 4, 0xff6fa1);
      barFill.setOrigin(0.5);

      this.listContainer!.add([bg, eggTxt, timeTxt, barBg, barFill]);

      // 단축 버튼
      const speedCost = EggHatcher.speedUpCost(i);
      const btnLabel = remainMin <= 0 ? '확인' : `⚡ 단축\n💰${speedCost}`;
      const btnBg = this.add.rectangle(x, listY + 76, 84, 28, 0xffd95c);
      btnBg.setStrokeStyle(2, 0xe0a020);
      const btnTxt = this.add.text(x, listY + 76, btnLabel, {
        fontFamily: Theme.font.family, fontSize: '10px', fontStyle: '800',
        color: '#3a2540', align: 'center',
      }).setOrigin(0.5);
      btnBg.setInteractive({ useHandCursor: true });
      btnBg.on('pointerover', () => btnBg.setFillStyle(0xffe98a));
      btnBg.on('pointerout', () => btnBg.setFillStyle(0xffd95c));
      btnBg.on('pointerdown', () => this.speedUpEgg(i));
      this.listContainer!.add([btnBg, btnTxt]);
    });

    // 스크롤 한계 계산
    const contentRight = cursorX;
    this.listMaxScrollX = Math.max(0, contentRight - listViewportRight);
    this.listScrollX = Phaser.Math.Clamp(this.listScrollX, 0, this.listMaxScrollX);
    this.listTargetX = this.listScrollX;
    this.listContainer!.x = -this.listScrollX;

    this._setupListScroll(listViewportLeft, listViewportRight, listViewportTop, listViewportH);
  }

  /** 펫 리스트 가로 스크롤 — 휠/드래그 + 부드러운 lerp */
  private _setupListScroll(left: number, right: number, top: number, height: number): void {
    // 이전 리스너 정리
    for (const ev of this.listScrollEvents) ev.off();
    this.listScrollEvents = [];

    const inViewport = (p: Phaser.Input.Pointer) =>
      p.x >= left && p.x <= right && p.y >= top && p.y <= top + height;

    const onWheel = (p: Phaser.Input.Pointer, _gos: unknown, dx: number, dy: number) => {
      if (!inViewport(p)) return;
      // 가로 휠 또는 세로 휠 모두 가로 스크롤로 변환
      const delta = (Math.abs(dx) > Math.abs(dy) ? dx : dy) * 0.9;
      this.listTargetX = Phaser.Math.Clamp(this.listTargetX + delta, 0, this.listMaxScrollX);
    };
    this.input.on('wheel', onWheel);
    this.listScrollEvents.push({ off: () => this.input.off('wheel', onWheel) });

    const onDown = (p: Phaser.Input.Pointer) => {
      if (!inViewport(p)) return;
      this.listDragging = true;
      this.listDragStartX = p.x;
      this.listDragStartScroll = this.listTargetX;
    };
    this.input.on('pointerdown', onDown);
    this.listScrollEvents.push({ off: () => this.input.off('pointerdown', onDown) });

    const onMove = (p: Phaser.Input.Pointer) => {
      if (!this.listDragging) return;
      const delta = this.listDragStartX - p.x;
      this.listTargetX = Phaser.Math.Clamp(this.listDragStartScroll + delta, 0, this.listMaxScrollX);
    };
    this.input.on('pointermove', onMove);
    this.listScrollEvents.push({ off: () => this.input.off('pointermove', onMove) });

    const onUp = () => { this.listDragging = false; };
    this.input.on('pointerup', onUp);
    this.input.on('pointerupoutside', onUp);
    this.listScrollEvents.push({ off: () => { this.input.off('pointerup', onUp); this.input.off('pointerupoutside', onUp); } });
  }

  update(_time: number, delta: number): void {
    // 부드러운 가로 스크롤 lerp
    if (this.listContainer && Math.abs(this.listTargetX - this.listScrollX) > 0.1) {
      const t = Math.min(1, (delta / 16) * 0.25);
      this.listScrollX += (this.listTargetX - this.listScrollX) * t;
      this.listContainer.x = -this.listScrollX;
    }
  }

  /** 부화 완료 축하 모달 (순서대로 한 마리씩) */
  private async _showHatchCelebration(hatched: { petId: string }[]): Promise<void> {
    AudioManager.playSfx('levelup');

    for (const h of hatched) {
      const meta = PETS_BY_ID[h.petId];
      if (!meta) continue;

      await new Promise<void>((resolve) => {
        // 어두운 오버레이
        const overlay = this.add.rectangle(GAME_WIDTH/2, GAME_HEIGHT/2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.65).setDepth(800);

        // 모달 카드
        const modal = this.add.rectangle(GAME_WIDTH/2, GAME_HEIGHT/2, 480, 440, 0xfff0f8).setDepth(801);
        modal.setStrokeStyle(4, 0xff6fa1);

        // 제목
        const title = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2 - 180, '🎉 부화!', {
          fontFamily: 'Pretendard Variable', fontSize: '40px', fontStyle: '800', color: '#ff6fa1',
        }).setOrigin(0.5).setDepth(802);
        this.tweens.add({ targets: title, scaleX: 1.1, scaleY: 1.1, duration: 400, yoyo: true, repeat: -1, ease: 'Bounce' });

        // 펫 이름 + 종류
        const rarityColor = meta.rarity === 'legend' ? '#d4a820' : meta.rarity === 'epic' ? '#9b6bff' : meta.rarity === 'rare' ? '#4499ee' : '#3a2540';
        const nameText = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2 + 100, `${meta.name}`, {
          fontFamily: 'Pretendard Variable', fontSize: '28px', fontStyle: '800', color: rarityColor,
        }).setOrigin(0.5).setDepth(802);

        const speciesEmoji: Record<string, string> = {
          dog: '🐶', cat: '🐱', rabbit: '🐰', hamster: '🐹', dragon: '🐲', hedgehog: '🦔', wolf: '🐺', whale: '🐳', chick: '🐥', ferret: '🐾', alpaca: '🦙', capybara: '🐹', cow: '🐮', goat: '🐐', crab: '🦀', bunny_alien: '👽', phoenix: '🔥',
          bird: '🐦', unicorn: '🦄', penguin: '🐧', fox: '🦊', panda: '🐼',
          bear: '🐻', pig: '🐷', turtle: '🐢', fish: '🐟',
        };
        const rarityLabel = meta.rarity === 'legend' ? '✨ 레전드 ✨' : meta.rarity === 'epic' ? '💜 에픽' : meta.rarity === 'rare' ? '💙 레어' : '⬜ 커먼';
        const rarityText = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2 + 140, rarityLabel, {
          fontFamily: 'Pretendard Variable', fontSize: '18px', fontStyle: '700', color: rarityColor,
        }).setOrigin(0.5).setDepth(802);

        const friendMsg = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2 + 178, '내 친구가 됐어요!', {
          fontFamily: 'Pretendard Variable', fontSize: '20px', fontStyle: '700', color: '#7a5b80',
        }).setOrigin(0.5).setDepth(802);

        // 이모지 대형 (SVG 렌더링 대신 큰 이모지)
        const bigEmoji = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2 - 40, speciesEmoji[meta.species] ?? '🐾', {
          fontSize: '96px',
        }).setOrigin(0.5).setDepth(802);
        this.tweens.add({ targets: bigEmoji, y: bigEmoji.y - 12, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.InOut' });

        // 계속 버튼
        const btnBg = this.add.rectangle(GAME_WIDTH/2, GAME_HEIGHT/2 + 230, 200, 52, 0xff6fa1).setDepth(802);
        btnBg.setStrokeStyle(3, 0xe0507a);
        const btnTxt = this.add.text(GAME_WIDTH/2, GAME_HEIGHT/2 + 230, '계속하기 →', {
          fontFamily: 'Pretendard Variable', fontSize: '20px', fontStyle: '800', color: '#ffffff',
        }).setOrigin(0.5).setDepth(803);
        btnBg.setInteractive({ useHandCursor: true });

        // 폭죽 파티클
        for (let p = 0; p < 20; p++) {
          this.time.delayedCall(p * 80, () => {
            const px = GAME_WIDTH/2 + (Math.random() - 0.5) * 460;
            const py = GAME_HEIGHT/2 + (Math.random() - 0.5) * 400;
            const colors = ['#ffd95c', '#ff6fa1', '#9b6bff', '#7fecff', '#a8f070'];
            const star = this.add.text(px, py, '✦', {
              fontSize: `${14 + Math.floor(Math.random() * 16)}px`,
              color: colors[Math.floor(Math.random() * colors.length)],
            }).setDepth(804).setOrigin(0.5);
            this.tweens.add({
              targets: star, alpha: 0, y: py - 60 - Math.random() * 80,
              scale: 0.2, duration: 900 + Math.random() * 600,
              ease: 'Sine.Out', onComplete: () => star.destroy(),
            });
          });
        }

        const cleanup = () => {
          overlay.destroy(); modal.destroy(); title.destroy();
          bigEmoji.destroy(); nameText.destroy(); rarityText.destroy();
          friendMsg.destroy(); btnBg.destroy(); btnTxt.destroy();
          resolve();
        };
        btnBg.on('pointerdown', cleanup);
        btnBg.on('pointerover', () => btnBg.setFillStyle(0xe0507a));
        btnBg.on('pointerout', () => btnBg.setFillStyle(0xff6fa1));
      });
    }
  }

  private _spawnHearts(cx: number, cy: number): void {
    const emojis = ['❤️', '💕', '💖'];
    for (let i = 0; i < 6; i++) {
      const t = this.add.text(
        cx + (Math.random() - 0.5) * 60,
        cy,
        emojis[Math.floor(Math.random() * emojis.length)],
        { fontSize: '24px' }
      ).setOrigin(0.5).setDepth(500);
      this.tweens.add({
        targets: t,
        y: cy - 80 - Math.random() * 60,
        x: t.x + (Math.random() - 0.5) * 80,
        alpha: 0,
        scale: 1.4,
        duration: 900 + Math.random() * 400,
        delay: Math.random() * 300,
        ease: 'Sine.Out',
        onComplete: () => t.destroy(),
      });
    }
  }

  private _spawnSparkles(cx: number, cy: number): void {
    const colors = [0xffd95c, 0xff6fa1, 0x9b6bff, 0xffffff];
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const r = this.add.rectangle(cx, cy, 10, 10, colors[i % colors.length]).setDepth(500);
      this.tweens.add({
        targets: r,
        x: cx + Math.cos(angle) * (60 + Math.random() * 40),
        y: cy + Math.sin(angle) * (60 + Math.random() * 40),
        alpha: 0,
        scale: 0.3,
        duration: 700 + Math.random() * 300,
        delay: Math.random() * 200,
        ease: 'Sine.Out',
        onComplete: () => r.destroy(),
      });
    }
  }

  private drawGauge(x: number, y: number, label: string, value: number, color: number): void {
    const lbl = this.add.text(x, y, label, { fontFamily: Theme.font.family, fontSize: '14px', fontStyle: '700', color: Theme.hex.text }).setOrigin(0, 0.5);
    const barW = 220;
    const bg = this.add.graphics();
    bg.fillStyle(0xeeeeee, 1);
    bg.fillRoundedRect(x + 110, y - 8, barW, 16, 8);
    const fillW = (value / 100) * barW;
    const fill = this.add.graphics();
    fill.fillStyle(color, 1);
    if (fillW > 0) fill.fillRoundedRect(x + 110, y - 8, fillW, 16, 8);
    const txt = this.add.text(x + 110 + barW + 12, y, `${Math.round(value)}`, { fontFamily: Theme.font.family, fontSize: '13px', fontStyle: '800', color: Theme.hex.text }).setOrigin(0, 0.5);
    this.gaugeContainer!.add([lbl, bg, fill, txt]);
  }

  private buyEgg(): void {
    if (!EconomyEngine.spend({ coins: 15000 })) {
      this.toast.show('💰 코인이 부족해요! (15000코인 필요)');
      return;
    }
    EggHatcher.startHatching(EGG_HATCH_DURATION_MS);
    this.refresh();
  }

  private speedUpEgg(eggIndex: number): void {
    const cost = EggHatcher.speedUpCost(eggIndex);
    if (cost <= 0) { this.refresh(); return; }
    if (!EconomyEngine.spend({ coins: cost })) {
      this.toast.show(`💰 단축에 ${cost}코인이 필요해요!`);
      return;
    }
    EggHatcher.finishNow(eggIndex);
    AudioManager.playSfx('click');
    this.toast.show(`✨ 부화 단축 완료! (-${cost}💰)`, 'gold');
    // 즉시 부화 처리
    const hatched = EggHatcher.checkReadyEggs();
    this.refresh().then(() => {
      if (hatched.length > 0) void this._showHatchCelebration(hatched);
    });
  }
}
